import apiClient from '../lib/apiClient';

// Get all job applications with optional filtering
const getApplications = async (filters = {}) => {
  try {
    const result = await apiClient.get('/job-applications', filters);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch applications' };
  }
};

// Get single application by ID
const getApplication = async (id) => {
  try {
    const result = await apiClient.get(`/job-applications/${id}`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch application' };
  }
};

// Create a new application
const createApplication = async (applicationData) => {
  try {
    const result = await apiClient.post('/job-applications', applicationData);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to create application' };
  }
};

// Update an application
const updateApplication = async (id, updates) => {
  try {
    const result = await apiClient.put(`/job-applications/${id}`, updates);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to update application' };
  }
};

// Delete an application
const deleteApplication = async (id) => {
  try {
    const result = await apiClient.delete(`/job-applications/${id}`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to delete application' };
  }
};

// Update application status
const updateStatus = async (id, status, notes = '') => {
  try {
    const result = await apiClient.put(`/job-applications/${id}/status`, {
      status,
      notes
    });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to update status' };
  }
};

// Get application statistics
const getStatistics = async () => {
  try {
    const result = await apiClient.get('/job-applications/statistics');
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch statistics' };
  }
};

// Get funnel data
const getFunnel = async (timeFrame = '30_days') => {
  try {
    const result = await apiClient.get('/job-applications/funnel', { timeFrame });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch funnel data' };
  }
};

// Get interactions for an application
const getInteractions = async (applicationId) => {
  try {
    const result = await apiClient.get(`/job-applications/${applicationId}/interactions`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch interactions' };
  }
};

// Get interviews for an application
const getInterviews = async (applicationId) => {
  try {
    const result = await apiClient.get(`/job-applications/${applicationId}/interviews`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch interviews' };
  }
};

// Add interaction/note
const addInteraction = async (applicationId, interactionData) => {
  try {
    const result = await apiClient.post(`/job-applications/${applicationId}/interactions`, interactionData);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to add interaction' };
  }
};
const runAtsScore = async (applicationId) => {
  try {
    const result = await apiClient.post(`/job-applications/${applicationId}/ats-score`, {});
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to score resume' };
  }
};
const moveStage = async (applicationId, stageData) => {
  try {
    const result = await apiClient.post(`/job-applications/${applicationId}/move-stage`, stageData);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to move stage' };
  }
};
const sendEmail = async (applicationId, emailData) => {
    try {
      const result = await apiClient.post(`/job-applications/${applicationId}/send-email`, emailData);
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Failed to send email' };
    }
  };

// Get validated leads from CRM
const getValidatedLeads = async () => {
  try {
    const result = await apiClient.get('/job-applications/get-validated-leads');
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch validated leads' };
  }
};

// Import applicants from validated leads
const importFromLeads = async (importData) => {
  try {
    const result = await apiClient.post('/job-applications/import-from-leads', importData);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to import from leads' };
  }
};

// Import applicants from Excel file
const importFromExcel = async (formData) => {
  try {
    const token = localStorage.getItem('erp_auth_token');
    if (!token) {
      return { success: false, error: 'Authentication required. Please log in again.' };
    }
    const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/job-applications/import-from-excel`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const result = await response.json();
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to import from Excel' };
  }
};

// Schedule an interview
const scheduleInterview = async (data) => {
  try {
    const result = await apiClient.post('/job-applications/schedule-interview', data);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to schedule interview' };
  }
};

// Update an interview
const updateInterview = async (interviewId, data) => {
  try {
    const result = await apiClient.put(`/job-applications/update-interview/${interviewId}`, data);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to update interview' };
  }
};

// Get documents for an applicant
const getDocuments = async (applicationId) => {
  try {
    const result = await apiClient.get(`/job-applications/${applicationId}/documents`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch documents' };
  }
};

// Upload document for an applicant
const uploadDocument = async (applicationId, file, documentType = 'Resume') => {
  console.log('uploadDocument called with:', { applicationId, fileName: file?.name, documentType });
  console.log('apiClient.baseURL:', apiClient.baseURL);
  if (!applicationId) {
    console.error('Missing applicationId!');
    return { success: false, error: 'Missing applicant ID' };
  }
  try {
    const token = apiClient.getToken();
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', documentType);

    const url = `${apiClient.baseURL}/job-applications/${applicationId}/documents`;
    console.log('Upload URL:', url);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    console.log('Upload response status:', response.status, response.statusText);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload error response:', errorText);
      throw new Error(errorText || 'Failed to upload document');
    }

    const result = await response.json();
    console.log('Upload success result:', result);
    return result;
  } catch (error) {
    console.error('Upload caught error:', error);
    return { success: false, error: error.message || 'Failed to upload document' };
  }
};

// Delete document
const deleteDocument = async (applicationId, documentId) => {
  try {
    const result = await apiClient.delete(`/job-applications/${applicationId}/documents/${documentId}`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to delete document' };
  }
};

// Get duplicate applications grouped by email/phone identity
const getDuplicates = async () => {
  try {
    const result = await apiClient.get('/job-applications/duplicates');
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch duplicate applications' };
  }
};

// Rescan and recalculate duplicate applications across the database
const recalculateDuplicates = async () => {
  try {
    const result = await apiClient.post('/job-applications/recalculate-duplicates', {});
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to recalculate duplicate applications' };
  }
};

// Run recruitment automation on applicant
const runAutomation = async (applicationId) => {
  try {
    const result = await apiClient.post(`/job-applications/${applicationId}/run-automation`, {});
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to execute recruitment automation' };
  }
};

// Get automation logs for applicant
const getAutomationLogs = async (applicationId) => {
  try {
    const result = await apiClient.get(`/job-applications/${applicationId}/automation-logs`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch automation logs' };
  }
};

// Get automation settings
const getAutomationSettings = async () => {
  try {
    const result = await apiClient.get('/job-applications/automation-settings');
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch automation settings' };
  }
};

// Update automation settings
const updateAutomationSettings = async (settings) => {
  try {
    const result = await apiClient.post('/job-applications/automation-settings', settings);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to update automation settings' };
  }
};

// Send offer letter to candidate
const sendOfferLetter = async (id, offerTerms = null) => {
  try {
    const result = await apiClient.post(`/job-applications/${id}/send-offer-letter`, offerTerms || {});
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to send offer letter' };
  }
};

// Record or update Final HR salary and offer terms
const updateOfferTerms = async (id, terms) => {
  try {
    const result = await apiClient.post(`/job-applications/${id}/offer-terms`, terms);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to save offer terms' };
  }
};

// Send candidate offer confirmation email & save pending terms
const sendOfferConfirmation = async (id, terms) => {
  try {
    const result = await apiClient.post(`/job-applications/${id}/send-offer-confirmation`, terms);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to send offer confirmation email' };
  }
};

// Fetch public candidate offer details using candidate token
const getPublicOfferDetails = async (token) => {
  try {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';
    const response = await fetch(`${API_BASE_URL}/offer-confirmation/${token}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch offer details' };
  }
};

// Public upload candidate verification document using token
const uploadPublicCandidateDocument = async (token, file, category) => {
  try {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    const response = await fetch(`${API_BASE_URL}/offer-confirmation/${token}/upload-document`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to upload document' };
  }
};

// Public accept offer & terms and conditions
const acceptPublicOffer = async (token, tcVersion = 'v1.0') => {
  try {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';
    const response = await fetch(`${API_BASE_URL}/offer-confirmation/${token}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tc_accepted: true, tc_version: tcVersion }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to accept offer' };
  }
};
// Verify candidate uploaded verification documents
const verifyCandidateDocuments = async (id) => {
  try {
    const result = await apiClient.post(`/job-applications/${id}/verify-candidate-documents`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to verify candidate documents' };
  }
};

// Release official offer letter PDF (after candidate acceptance & document verification)
const releaseOfficialOfferLetter = async (id) => {
  try {
    const result = await apiClient.post(`/job-applications/${id}/release-official-offer-letter`);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to release official offer letter' };
  }
};

export default {
  getApplications,
  getApplication,
  createApplication,
  updateApplication,
  deleteApplication,
  updateStatus,
  getStatistics,
  getFunnel,
  getInteractions,
  getInterviews,
  addInteraction,
  sendEmail,
  moveStage,
  sendOfferLetter,
  updateOfferTerms,
  sendOfferConfirmation,
  getPublicOfferDetails,
  uploadPublicCandidateDocument,
  acceptPublicOffer,
  verifyCandidateDocuments,
  releaseOfficialOfferLetter,
  runAtsScore,
  runAutomation,
  getAutomationLogs,
  getAutomationSettings,
  updateAutomationSettings,
  getValidatedLeads,
  importFromLeads,
  importFromExcel,
  scheduleInterview,
  updateInterview,
  getDocuments,
  uploadDocument,
  deleteDocument,
  getDuplicates,
  recalculateDuplicates
};
